import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useFadeIn(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => setVisible(true), delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );
    observer.observe(el);
    return () => { observer.unobserve(el); clearTimeout(timer); };
  }, [delay]);
  return [ref, visible];
}

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconArrow({ className = 'w-4 h-4' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M5 12h14M12 5l7 7-7 7" /></svg>;
}
function IconCheckCircle() {
  return <svg viewBox="0 0 24 24" className="w-6 h-6" {...S}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>;
}

export default function FreeReview() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError]               = useState(null);
  const [scrolled, setScrolled]         = useState(false);

  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 40);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    if (!showThankYou) return;
    const id = setTimeout(() => setShowThankYou(false), 5000);
    return () => clearTimeout(id);
  }, [showThankYou]);

  const [formRef, formVisible] = useFadeIn(0);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.target;
    const fd   = new FormData(form);
    fd.append('access_key', process.env.REACT_APP_WEB3FORMS_KEY);
    try {
      const res  = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (data.success) { form.reset(); setShowThankYou(true); }
      else setError('Something went wrong. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fc = (v) =>
    `transition-all duration-700 ease-out ${v ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`;

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white text-slate-900 selection:bg-emerald-200/60">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ease-in-out ${
          scrolled
            ? 'bg-white/75 border-b border-black/[0.07] shadow-sm shadow-black/[0.04]'
            : 'bg-transparent border-b border-white/[0.06]'
        }`}
        style={{ backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)' }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2.5 group">
            <svg
              viewBox="0 0 512 512"
              className={`h-10 w-10 group-hover:scale-105 transition-all duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}
              fill="none" stroke="currentColor" strokeWidth={38} strokeLinecap="round" strokeLinejoin="round"
            >
              <g transform="translate(256,256)">
                <circle cx="0" cy="0" r="140" />
                <path d="M -50 105 L -50 -105" />
                <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
              </g>
            </svg>
            <span className={`text-lg font-semibold tracking-tight transition-colors duration-500 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
              Peronyx
            </span>
          </Link>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative pt-40 pb-20 flex flex-col items-center text-center overflow-hidden bg-slate-950 px-6">
        <div className="absolute inset-0 hero-color-drift" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 to-slate-950/90" />
        <div className="hero-blob absolute -top-32 left-1/2 h-96 w-[44rem] rounded-full blur-3xl bg-gradient-to-tr from-emerald-500 to-sky-400 pointer-events-none" />

        <div className="relative z-10 max-w-2xl">
          <p className="hero-fade-up text-xs font-semibold tracking-[0.28em] text-emerald-400 uppercase mb-4" style={{ animationDelay: '60ms' }}>
            Free · No Obligation
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.06] text-white">
            <span className="inline-flex flex-wrap justify-center gap-x-[0.3em]">
              {['Get', 'Your', 'Free'].map((word, i) => (
                <span key={word} className="word-rise" style={{ animationDelay: `${160 + i * 100}ms` }}>{word}</span>
              ))}
            </span>
            <span className="word-rise-breathe block mt-1">
              <span className="shimmer-text">Website Review</span>
            </span>
          </h1>
          <p className="hero-fade-up mt-6 text-slate-300 text-lg leading-relaxed max-w-xl mx-auto" style={{ animationDelay: '760ms' }}>
            Send us your info and we'll take a look at your current online presence and tell you exactly what it's missing.
          </p>
        </div>
      </section>

      {/* ── Form ───────────────────────────────────────────────────────────── */}
      <section className="py-16 px-6 bg-[#f5f5f7]">
        <div ref={formRef} className={`mx-auto max-w-lg ${fc(formVisible)}`}>
          <div className="bg-white rounded-3xl shadow-sm ring-1 ring-black/[0.04] p-8 md:p-10">
            <form onSubmit={onSubmit} className="space-y-4">
              <input type="checkbox" name="botcheck" className="hidden" aria-hidden="true" />

              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Business Name</span>
                <input
                  name="business_name"
                  required
                  placeholder="Your business name"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Your Name</span>
                <input
                  name="name"
                  required
                  placeholder="Your name"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Email</span>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@yourbusiness.com"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">
                  Phone <span className="normal-case tracking-normal font-normal text-slate-400">(optional)</span>
                </span>
                <input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(555) 000-0000"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>

              <label className="block">
                <span className="text-[11px] font-semibold tracking-widest text-slate-500 uppercase">Current Website</span>
                <input
                  name="website"
                  type="text"
                  required
                  placeholder="yoursite.com"
                  className="mt-2 w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                />
              </label>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-sweep w-full mt-2 rounded-xl border border-emerald-500 text-emerald-600 hover:text-white px-5 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {isSubmitting ? 'Sending…' : (
                  <>
                    Send My Info
                    <span className="inline-flex"><IconArrow /></span>
                  </>
                )}
              </button>
              {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            </form>

            <p className="mt-6 text-center text-xs text-slate-400 leading-relaxed">
              No sales pressure. We'll reach out with honest feedback.
            </p>
          </div>
        </div>
      </section>

      {/* ── Thank-you modal ─────────────────────────────────────────────────── */}
      {showThankYou && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 backdrop-blur-sm backdrop-enter">
          <div className="mx-4 max-w-sm w-full rounded-2xl bg-white p-8 shadow-2xl text-center border border-slate-200 modal-enter">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <IconCheckCircle />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">You're all set!</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              We got your info. We'll be in touch with honest feedback.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 active:scale-[0.97] transition-all duration-150"
            >
              Back to Home
            </Link>
          </div>
        </div>
      )}

    </div>
  );
}
